import { CITIES, type City } from "../core/cities";
import { cached } from "../lib/cache";

export interface CityNow {
  city: City;
  temp: number;
  cloud: number;
  code: number;
  wind: number;
  isDay: boolean;
  tz: string;
  /** Смещение от UTC в секундах: местное время считаем сами, чтобы кэш не врал */
  utcOffset: number;
  /** 24 часа температуры — на спарклайн в плитке */
  curve: number[];
}

/** Местное время города прямо сейчас. Не берём из ответа — он может быть из кэша. */
export function localTimeOf(utcOffset: number): string {
  const d = new Date(Date.now() + utcOffset * 1000);
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

/**
 * Погода по всем городам ленты — ОДНИМ запросом.
 * Open-Meteo принимает списки координат через запятую и возвращает массив;
 * проверено 2026-09-10. Иначе тридцать городов стоили бы тридцать запросов.
 */
/**
 * Ряд городов — 96% всего расхода вызовов Open-Meteo (замер: 30 из 31.4 на
 * открытие страницы, потому что каждая локация считается отдельным вызовом).
 * Погода в них меняется раз в час, так что держим ответ полчаса: повторное
 * открытие обходится в 1.4 вызова вместо 31.4.
 */
export async function fetchCitiesNow(): Promise<CityNow[]> {
  const data = await cached("cities", 30 * 60 * 1000, fetchCitiesFresh);
  // Список городов мог смениться между версиями — тогда кэш не подходит.
  return data.length === CITIES.length ? data : fetchCitiesFresh();
}

async function fetchCitiesFresh(): Promise<CityNow[]> {
  const lat = CITIES.map((c) => c.lat).join(",");
  const lon = CITIES.map((c) => c.lon).join(",");
  const url =
    `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
    `&current=temperature_2m,weather_code,cloud_cover,wind_speed_10m,is_day` +
    `&hourly=temperature_2m&forecast_hours=24&timezone=auto`;

  const r = await fetch(url);
  if (!r.ok) throw new Error(`Open-Meteo ответил ${r.status}`);
  const raw = await r.json();
  const list = Array.isArray(raw) ? raw : [raw];

  const out = list.map((x, i): CityNow => {
    const c = x.current;
    return {
      city: CITIES[i]!,
      temp: c.temperature_2m,
      cloud: c.cloud_cover,
      code: c.weather_code,
      wind: c.wind_speed_10m,
      isDay: !!c.is_day,
      tz: x.timezone,
      utcOffset: x.utc_offset_seconds ?? 0,
      curve: (x.hourly?.temperature_2m ?? []).filter((v: number | null) => v != null),
    };
  });
  return out;
}
